import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { ArrowLeft, Save, Trash2, Plus, Zap, Calendar as CalendarIcon, ExternalLink, Landmark, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { GBP, INVOICE_STATUS } from '../invoices/constants';

const FREQ = [{ value: 'monthly', label: 'Monthly' }, { value: 'quarterly', label: 'Quarterly' }, { value: 'annually', label: 'Annually' }];

function fmtDate(iso) {
    if (!iso) return null;
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function DatePickerField({ label, value, onChange, testId, allowClear }) {
    const [open, setOpen] = useState(false);
    const dateVal = value ? new Date(value + 'T12:00:00') : undefined;
    const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return (
        <div>
            <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">{label}</Label>
            <div className="flex gap-1.5">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button variant="outline" className="mt-1.5 h-9 flex-1 justify-start text-left font-normal text-[13px]" data-testid={testId}>
                            <CalendarIcon size={14} className="mr-2 text-[color:var(--bm-text-subtle)]" />
                            {value ? fmtDate(value) : <span className="text-[color:var(--bm-text-subtle)]">Pick a date</span>}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={dateVal} initialFocus onSelect={d => { if (d) { onChange(toIso(d)); setOpen(false); } }} />
                    </PopoverContent>
                </Popover>
                {allowClear && value && <Button variant="ghost" className="mt-1.5 h-9 px-2 text-[color:var(--bm-text-subtle)]" onClick={() => onChange(null)}>Clear</Button>}
            </div>
        </div>
    );
}
function Card({ title, children, actions }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="font-display text-[15px] font-semibold">{title}</div>
                {actions}
            </div>
            {children}
        </div>
    );
}

const EMPTY = {
    client_id: '', contract_id: null, title: 'Recurring invoice', line_items: [],
    vat_rate: 20, frequency: 'monthly', billing_day: 1, last_day_of_month: false,
    start_date: new Date().toISOString().slice(0, 10), end_date: null, max_occurrences: null,
    payment_terms_days: 14, payment_link_url: '', payment_link_provider: null,
    auto_send: false, send_to: '', notes: '', status: 'active',
};

export default function RecurringDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const isNew = id === 'new';
    const [s, setS] = useState(null);
    const [clients, setClients] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [generated, setGenerated] = useState([]);
    const [saving, setSaving] = useState(false);

    const update = (patch) => setS(v => ({ ...v, ...patch }));

    const load = useCallback(async () => {
        const cl = await api.get('/clients', { params: { per_page: 200 } }).then(r => r.data.items).catch(() => []);
        setClients(cl);
        if (isNew) {
            setS({ ...EMPTY, client_id: sp.get('client_id') || '', contract_id: sp.get('contract_id') || null });
        } else {
            try {
                const r = await api.get(`/recurring-invoices/${id}`);
                const data = r.data;
                // Back-fill the dual payment fields from the legacy single link
                if (!data.bank_transfer_url && !data.card_payment_url && data.payment_link_url) {
                    if (data.payment_link_provider === 'stripe' || data.payment_link_provider === 'gocardless') {
                        data.card_payment_url = data.payment_link_url;
                        data.card_payment_provider = data.payment_link_provider;
                    } else {
                        data.bank_transfer_url = data.payment_link_url;
                    }
                }
                setS(data);
                setGenerated(data.invoices || []);
            } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); navigate('/recurring'); }
        }
    }, [id, isNew, navigate, sp]);
    useEffect(() => { load(); }, [load]);

    // Load contracts for the chosen client (for "pull monthly fee")
    useEffect(() => {
        if (!s?.client_id) { setContracts([]); return; }
        api.get('/contracts', { params: { client_id: s.client_id, per_page: 100 } })
            .then(r => setContracts(r.data.items)).catch(() => setContracts([]));
    }, [s?.client_id]);

    const live = useMemo(() => {
        if (!s) return { subtotal: 0, vat_amount: 0, total: 0 };
        const rate = Number(s.vat_rate ?? 20);
        let sub = 0, vat = 0;
        for (const li of (s.line_items || [])) {
            const line = Math.round((Number(li.qty) || 0) * (Number(li.unit_price) || 0) * 100) / 100;
            sub = Math.round((sub + line) * 100) / 100;
            if (li.vat_applies !== false) vat = Math.round((vat + line * rate / 100) * 100) / 100;
        }
        return { subtotal: sub, vat_amount: vat, total: Math.round((sub + vat) * 100) / 100 };
    }, [s]);

    if (!s) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;

    const addLine = () => update({ line_items: [...(s.line_items || []), { description: '', qty: 1, unit_price: 0, vat_applies: true }] });
    const updateLine = (idx, patch) => { const li = [...s.line_items]; li[idx] = { ...li[idx], ...patch }; update({ line_items: li }); };
    const removeLine = (idx) => update({ line_items: s.line_items.filter((_, i) => i !== idx) });

    const pullFromContract = () => {
        const c = contracts.find(x => x.id === s.contract_id);
        if (!c) return toast.error('Select a contract first');
        const opt = c.accepted_option_snapshot?.name || 'subscription';
        update({
            line_items: [{ description: `Monthly support — ${opt} (${c.contract_number})`, qty: 1, unit_price: Number(c.monthly_saas_fee || 0), vat_applies: true }],
            title: s.title === 'Recurring invoice' ? `Monthly billing — ${c.contract_number}` : s.title,
        });
        toast.success('Pulled monthly fee from contract');
    };

    const buildBody = () => ({
        client_id: s.client_id,
        contract_id: s.contract_id || null,
        title: s.title?.trim() || 'Recurring invoice',
        line_items: (s.line_items || []).map(li => ({ description: li.description, qty: Number(li.qty) || 0, unit_price: Number(li.unit_price) || 0, vat_applies: li.vat_applies !== false })),
        vat_rate: Number(s.vat_rate ?? 20),
        frequency: s.frequency,
        billing_day: Number(s.billing_day) || 1,
        last_day_of_month: !!s.last_day_of_month,
        start_date: s.start_date,
        end_date: s.end_date || null,
        max_occurrences: s.max_occurrences ? Number(s.max_occurrences) : null,
        payment_terms_days: Number(s.payment_terms_days) || 14,
        bank_transfer_url: s.bank_transfer_url || null,
        card_payment_url: s.card_payment_url || null,
        card_payment_provider: s.card_payment_provider || null,
        auto_send: !!s.auto_send,
        send_to: s.send_to || null,
        notes: s.notes || null,
    });

    const save = async () => {
        if (!s.client_id) return toast.error('Select a client');
        if (!(s.line_items || []).length) return toast.error('Add at least one line item');
        setSaving(true);
        try {
            if (isNew) {
                const r = await api.post('/recurring-invoices', buildBody());
                toast.success('Recurring schedule created');
                navigate(`/recurring/${r.data.id}`);
            } else {
                const r = await api.patch(`/recurring-invoices/${id}`, buildBody());
                setS({ ...r.data, invoices: generated });
                toast.success('Schedule saved');
            }
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const generateNow = async () => {
        if (!confirm('Generate the next invoice now? This advances the schedule to the following period.')) return;
        try {
            const r = await api.post(`/recurring-invoices/${id}/generate-now`);
            if (r.data.skipped) { toast.message(r.data.message); return; }
            toast.success(`Generated ${r.data.invoice.invoice_number}`);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    const remove = async () => {
        if (!confirm('Delete this recurring schedule? Already-generated invoices are kept.')) return;
        try { await api.delete(`/recurring-invoices/${id}`); toast.success('Schedule deleted'); navigate('/recurring'); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };

    return (
        <div data-testid="recurring-detail-page" className="bm-fade-in">
            <PageHeader
                title={isNew ? 'New recurring schedule' : s.title}
                description={isNew ? 'Auto-generate invoices on a cadence' : `${s.client_name} · ${s.frequency} · next ${s.status === 'completed' ? '—' : s.next_run_date}`}
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => navigate('/recurring')} className="h-9" data-testid="recurring-back-btn">
                            <ArrowLeft size={15} className="mr-1" /> Back
                        </Button>
                        {!isNew && (
                            <Button variant="outline" onClick={generateNow} className="h-9" data-testid="recurring-generate-now-btn">
                                <Zap size={15} className="mr-1" /> Generate next now
                            </Button>
                        )}
                        <Button onClick={save} disabled={saving} className="h-9" style={{ background: 'var(--bm-primary)' }} data-testid="recurring-save-btn">
                            <Save size={15} className="mr-1" /> {saving ? 'Saving…' : (isNew ? 'Create schedule' : 'Save')}
                        </Button>
                        {!isNew && (
                            <Button variant="outline" onClick={remove} className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid="recurring-delete-btn">
                                <Trash2 size={15} />
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                <div className="lg:col-span-2 space-y-5">
                    <Card title="Schedule basics">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Client *</Label>
                                <Select value={s.client_id} onValueChange={v => update({ client_id: v, contract_id: null })} disabled={!isNew}>
                                    <SelectTrigger className="mt-1.5 h-9" data-testid="recurring-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Title</Label>
                                <Input value={s.title || ''} onChange={e => update({ title: e.target.value })} className="mt-1.5 h-9" data-testid="recurring-title-input" />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Link to contract <span className="font-normal text-[color:var(--bm-text-subtle)]">(optional)</span></Label>
                                <div className="flex gap-2">
                                    <Select value={s.contract_id || '_none_'} onValueChange={v => update({ contract_id: v === '_none_' ? null : v })}>
                                        <SelectTrigger className="mt-1.5 h-9 flex-1" data-testid="recurring-contract-select"><SelectValue placeholder="No contract" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="_none_">No contract</SelectItem>
                                            {contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.contract_number} · {GBP(c.monthly_saas_fee)}/mo</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    {s.contract_id && <Button variant="outline" className="mt-1.5 h-9" onClick={pullFromContract} data-testid="recurring-pull-contract-btn">Use monthly fee</Button>}
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card title="Line items" actions={<Button size="sm" variant="outline" onClick={addLine} data-testid="recurring-add-line-btn"><Plus size={13} className="mr-1" /> Add line</Button>}>
                        <div className="space-y-2">
                            <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-[color:var(--bm-text-subtle)] font-semibold px-1">
                                <div className="col-span-6">Description</div><div className="col-span-1">Qty</div><div className="col-span-2">Unit £</div><div className="col-span-2 text-right">Line</div><div className="col-span-1"></div>
                            </div>
                            {(s.line_items || []).map((li, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`recurring-line-${idx}`}>
                                    <div className="col-span-6"><Input value={li.description || ''} onChange={e => updateLine(idx, { description: e.target.value })} className="h-9" placeholder="e.g. Monthly support" /></div>
                                    <div className="col-span-1"><Input type="number" step="0.1" value={li.qty ?? 1} onChange={e => updateLine(idx, { qty: e.target.value })} className="h-9" /></div>
                                    <div className="col-span-2"><Input type="number" step="0.01" value={li.unit_price ?? 0} onChange={e => updateLine(idx, { unit_price: e.target.value })} className="h-9" /></div>
                                    <div className="col-span-2 text-right text-[13px] font-medium">{GBP((Number(li.qty) || 0) * (Number(li.unit_price) || 0))}</div>
                                    <div className="col-span-1 text-right"><button onClick={() => removeLine(idx)} className="text-[color:var(--bm-text-subtle)] hover:text-red-600 p-1"><Trash2 size={14} /></button></div>
                                </div>
                            ))}
                            {(s.line_items || []).length === 0 && <div className="text-[12.5px] text-[color:var(--bm-text-muted)] py-3 text-center">No line items — add one to bill on schedule.</div>}
                        </div>
                        <div className="mt-4 pt-3 border-t border-[color:var(--bm-border)] grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] max-w-xs ml-auto">
                            <div className="text-[color:var(--bm-text-muted)]">Subtotal</div><div className="text-right">{GBP(live.subtotal)}</div>
                            <div className="text-[color:var(--bm-text-muted)]">VAT ({Number(s.vat_rate ?? 20)}%)</div><div className="text-right">{GBP(live.vat_amount)}</div>
                            <div className="font-semibold text-[color:var(--bm-primary)]">Per-invoice total</div><div className="text-right font-semibold text-[color:var(--bm-primary)]">{GBP(live.total)}</div>
                        </div>
                    </Card>

                    {!isNew && (
                        <Card title={`Generated invoices (${generated.length})`}>
                            {generated.length === 0 ? (
                                <div className="text-[12.5px] text-[color:var(--bm-text-muted)] py-4 text-center">None generated yet.</div>
                            ) : (
                                <div className="space-y-1">
                                    {generated.map(i => (
                                        <button key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}
                                                className="w-full flex items-center justify-between text-[12.5px] py-1.5 px-2 rounded hover:bg-[color:var(--bm-bg-mute)]"
                                                data-testid={`recurring-invoice-${i.id}`}>
                                            <span><span className="font-medium">{i.invoice_number}</span> · <span className="text-[color:var(--bm-text-muted)]">{i.issue_date}</span></span>
                                            <span className="flex items-center gap-2">
                                                <span className="text-[color:var(--bm-text-muted)]">{GBP(i.total)}</span>
                                                <span className={`bm-pill text-[10px] ${INVOICE_STATUS[i.status]?.tone || ''} border`}>{INVOICE_STATUS[i.status]?.label || i.status}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </Card>
                    )}
                </div>

                <div className="space-y-5">
                    <Card title="Cadence">
                        <div className="space-y-3">
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Frequency</Label>
                                <Select value={s.frequency} onValueChange={v => update({ frequency: v })}>
                                    <SelectTrigger className="mt-1.5 h-9" data-testid="recurring-frequency-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{FREQ.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-center justify-between rounded-md border border-[color:var(--bm-border)] px-3 py-2">
                                <div>
                                    <div className="text-[12.5px] font-medium">Bill on last day of month</div>
                                    <div className="text-[11px] text-[color:var(--bm-text-subtle)]">e.g. 28/29/30/31 automatically</div>
                                </div>
                                <Switch checked={!!s.last_day_of_month} onCheckedChange={v => update({ last_day_of_month: v })} data-testid="recurring-lastday-switch" />
                            </div>
                            {!s.last_day_of_month && (
                                <div>
                                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Billing day of month (1–31)</Label>
                                    <Input type="number" min="1" max="31" value={s.billing_day ?? 1} onChange={e => update({ billing_day: e.target.value })} className="mt-1.5 h-9" data-testid="recurring-billing-day-input" />
                                </div>
                            )}
                            <DatePickerField label="Start date" value={s.start_date} onChange={v => update({ start_date: v })} testId="recurring-start-date" />
                            <DatePickerField label="End date (optional)" value={s.end_date} onChange={v => update({ end_date: v })} testId="recurring-end-date" allowClear />
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Max invoices (optional)</Label>
                                <Input type="number" min="1" value={s.max_occurrences ?? ''} onChange={e => update({ max_occurrences: e.target.value })} className="mt-1.5 h-9" placeholder="Unlimited" data-testid="recurring-max-occ-input" />
                            </div>
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Payment terms (days)</Label>
                                <Input type="number" min="0" value={s.payment_terms_days ?? 14} onChange={e => update({ payment_terms_days: e.target.value })} className="mt-1.5 h-9" data-testid="recurring-terms-input" />
                            </div>
                        </div>
                    </Card>

                    <Card title="Delivery & payment">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between rounded-md border border-[color:var(--bm-border)] px-3 py-2">
                                <div>
                                    <div className="text-[12.5px] font-medium">Auto-send to customer</div>
                                    <div className="text-[11px] text-[color:var(--bm-text-subtle)]">Emails the invoice + PDF on generation. Off = creates a draft to review.</div>
                                </div>
                                <Switch checked={!!s.auto_send} onCheckedChange={v => update({ auto_send: v })} data-testid="recurring-autosend-switch" />
                            </div>
                            {s.auto_send && (
                                <div>
                                    <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Send to email (optional)</Label>
                                    <Input value={s.send_to || ''} onChange={e => update({ send_to: e.target.value })} className="mt-1.5 h-9" placeholder="Defaults to primary contact" data-testid="recurring-sendto-input" />
                                </div>
                            )}
                            <div className="pt-1 border-t border-[color:var(--bm-border)]">
                                <Label className="text-[12px] font-semibold text-[color:var(--bm-text)]">Payment options</Label>
                                <p className="text-[11px] text-[color:var(--bm-text-subtle)] mt-0.5">Set once here — applied to every invoice this schedule generates. Add either or both.</p>
                            </div>
                            {/* Option 1 — Bank transfer (Monzo) */}
                            <div className="rounded-lg border border-[color:var(--bm-border)] p-3">
                                <div className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-text)] mb-1.5">
                                    <Landmark size={13} className="text-[color:var(--bm-primary)]" /> Bank transfer (Monzo)
                                </div>
                                <Input value={s.bank_transfer_url || ''} onChange={e => update({ bank_transfer_url: e.target.value })}
                                       className="h-9" placeholder="https://monzo.me/…"
                                       data-testid="recurring-bank-transfer-input" />
                                {s.bank_transfer_url && (
                                    <a href={s.bank_transfer_url} target="_blank" rel="noopener noreferrer"
                                       className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-primary)] hover:underline"
                                       data-testid="recurring-bank-transfer-open">
                                        <ExternalLink size={12} /> Open bank transfer link
                                    </a>
                                )}
                            </div>
                            {/* Option 2 — Card / Direct Debit */}
                            <div className="rounded-lg border border-[color:var(--bm-border)] p-3">
                                <div className="flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-text)] mb-1.5">
                                    <CreditCard size={13} className="text-[color:var(--bm-accent-600)]" /> Card / Direct Debit
                                </div>
                                <Select value={s.card_payment_provider || '_none_'} onValueChange={v => update({ card_payment_provider: v === '_none_' ? null : v })}>
                                    <SelectTrigger className="h-9" data-testid="recurring-card-provider-select">
                                        <SelectValue placeholder="Choose provider…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="_none_">None</SelectItem>
                                        <SelectItem value="stripe">Stripe (card)</SelectItem>
                                        <SelectItem value="gocardless">GoCardless (Direct Debit)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Input value={s.card_payment_url || ''} onChange={e => update({ card_payment_url: e.target.value })}
                                       className="h-9 mt-2" placeholder="https://buy.stripe.com/… or pay.gocardless.com/…"
                                       data-testid="recurring-card-payment-input" />
                                {s.card_payment_url && (
                                    <a href={s.card_payment_url} target="_blank" rel="noopener noreferrer"
                                       className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--bm-accent-600)] hover:underline"
                                       data-testid="recurring-card-payment-open">
                                        <ExternalLink size={12} /> Open payment link
                                    </a>
                                )}
                            </div>
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">VAT rate (%)</Label>
                                <Input type="number" value={s.vat_rate ?? 20} onChange={e => update({ vat_rate: e.target.value })} className="mt-1.5 h-9" data-testid="recurring-vat-input" />
                            </div>
                            <div>
                                <Label className="text-[12px] font-medium text-[color:var(--bm-text-muted)]">Notes (on each invoice)</Label>
                                <Textarea value={s.notes || ''} onChange={e => update({ notes: e.target.value })} className="mt-1.5" rows={2} />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
}
